/* Audit Assistant — top-level container.
 *
 * Renders a floating launcher button (bottom-right) and a right-side
 * sliding chat panel. Frontend-only: all responses come from the
 * injected ChatProvider (MockChatProvider by default). Mounted once in
 * ProtectedLayout so it's available on every authenticated page.
 *
 * The panel is position:fixed and does NOT affect page layout/flow.
 */

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { useMockChat } from '../../hooks/useMockChat';
import { MOCK_PROJECTS } from '../../mock/mockResponses';
import { listProjects } from '../../services/api';
import { ChatHeader } from './ChatHeader';
import { ChatWindow } from './ChatWindow';
import { ChatInput } from './ChatInput';
import '../../styles/audit-assistant.css';

interface ProjectOption {
  id: string;
  name: string;
}

/** Pull an AUDIT-YYYY-NNNN id out of the current path (project/report routes). */
function detectAuditId(pathname: string): string | null {
  const m = pathname.match(/\/projects\/(AUDIT-\d{4}-\d+)/i);
  return m ? m[1] : null;
}

export function AuditAssistant(): JSX.Element {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const chat = useMockChat();

  // Project list — real projects from the API, with the mock list as a
  // fallback if the call fails or returns nothing.
  const [projects, setProjects] = useState<ProjectOption[]>(() =>
    MOCK_PROJECTS.map((p) => ({ id: p, name: p })),
  );
  const [selectedId, setSelectedId] = useState<string>(() => MOCK_PROJECTS[0]);

  // Fetch real projects once (auth token is present inside ProtectedLayout).
  useEffect(() => {
    let cancelled = false;
    void listProjects({ limit: 100 })
      .then((res) => {
        if (cancelled || !res.projects?.length) return;
        const opts: ProjectOption[] = res.projects.map((p) => ({
          id: p.audit_id,
          name: p.project_name || p.target_entity || p.audit_id,
        }));
        setProjects(opts);
        // Prefer the project in the current URL, else the first one.
        const urlId = detectAuditId(location.pathname);
        setSelectedId(opts.find((o) => o.id === urlId)?.id ?? opts[0].id);
      })
      .catch(() => {
        /* keep mock fallback */
      });
    return () => {
      cancelled = true;
    };
    // Fetch once on mount; URL changes are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-detect: when navigating to a project/report page, pre-select it.
  useEffect(() => {
    const urlId = detectAuditId(location.pathname);
    if (urlId && projects.some((o) => o.id === urlId)) {
      setSelectedId(urlId);
    }
  }, [location.pathname, projects]);

  // Keep the chat's grounding project (used for {project} substitution +
  // input placeholder) in sync with the selection.
  const selectedName = projects.find((o) => o.id === selectedId)?.name ?? selectedId;
  useEffect(() => {
    chat.setProject(selectedName);
  }, [selectedName, chat.setProject]);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (prompt: string): void => chat.send(prompt);

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        className={`aa-fab${open ? ' aa-fab-hidden' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Open Audit Assistant"
        data-tip="Audit Assistant"
      >
        <span className="aa-fab-icon" aria-hidden="true">💬</span>
        <span className="aa-fab-label">Audit Assistant</span>
      </button>

      {/* Backdrop (click to close) */}
      <div
        className={`aa-backdrop${open ? ' aa-open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Sliding panel */}
      <aside
        className={`aa-panel${open ? ' aa-open' : ''}`}
        role="dialog"
        aria-modal="false"
        aria-label="Audit Assistant"
        aria-hidden={!open}
      >
        <ChatHeader
          onNewChat={chat.newChat}
          onClear={chat.clear}
          onClose={() => setOpen(false)}
        />

        {/* Project context selector — the assistant is app-wide, so the
            analyst picks which project it's grounded on. */}
        <div className="aa-context">
          <label className="aa-context-label" htmlFor="aa-project">Project</label>
          <span className="aa-select">
            <select
              id="aa-project"
              className="aa-context-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {projects.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </span>
          <span className="aa-context-hint">Answers are grounded on this project</span>
        </div>

        <ChatWindow
          messages={chat.messages}
          typing={chat.typing}
          onPick={pick}
          onRegenerate={chat.regenerate}
        />
        <ChatInput
          value={chat.input}
          onChange={chat.setInput}
          onSend={() => chat.send()}
          disabled={chat.typing}
          placeholder={`Ask about ${chat.project}…`}
        />
      </aside>
    </>
  );
}
